import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);
const KEY = "uce_cart";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(KEY) || "[]");
        } catch {
            return [];
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(KEY, JSON.stringify(items));
    }, [items]);

    const add = useCallback((product, qty = 1) => {
        setItems((prev) => {
            const found = prev.find((i) => i.product_id === product.id);
            if (found) {
                return prev.map((i) =>
                    i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i
                );
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    name: product.name,
                    price: product.price,
                    emoji: product.emoji || "🌿",
                    image: product.images?.[0] || null,
                    quantity: qty,
                },
            ];
        });
        setOpen(true);
    }, []);

    const remove = useCallback((product_id) => {
        setItems((prev) => prev.filter((i) => i.product_id !== product_id));
    }, []);

    const update = useCallback((product_id, quantity) => {
        setItems((prev) =>
            prev
                .map((i) => (i.product_id === product_id ? { ...i, quantity } : i))
                .filter((i) => i.quantity > 0)
        );
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const count = items.reduce((s, i) => s + i.quantity, 0);

    const value = useMemo(
        () => ({ items, add, remove, update, clear, subtotal, count, open, setOpen }),
        [items, add, remove, update, clear, subtotal, count, open]
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
